import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
  useQuery,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

import { CraveErrorCode } from '@/enums/errorCodes';
import { getStoredProfile, nowInSeconds, refreshToken } from '@/services/auth';
import { SessionContext, UserAccount } from '@/services/graphQL/api/generated/types_web';
import { parseMediaIdFromContentUrl } from '@/utils/contentUrl';
import { getCraveError } from '@/utils/errors';
import { flush } from '@/utils/memoryCache';

import { log } from '../appMonitoringSuite';
import { useGraphQLQuery } from './hooks/useGraphQLQuery';
import { useLazyGraphQLQuery } from './hooks/useLazyGraphQLQuery';
import { APOLLO_CLIENT_CACHE_POLICY_OPTIONS } from './policy';
import { appQuery } from './queries/app';
import { getMediaByIDsQuery, getShowPageMediaByIDsQuery } from './queries/media';
import { getDeferredMediaByIDsQuery } from './queries/mediaDeferred';
import {
  getMoreInfoMediaByIDsQuery,
  getMoreInfoQueryVariables,
} from './queries/moreInfo';
import { onboardingQuery } from './queries/onboarding';
import { getPostersByIdsQuery } from './queries/posterByIds';
import { productIntroQuery } from './queries/productIntro';
import { userInformationQuery } from './queries/userInformation';
import { queryKeys } from './queryKeys';
import {
  AppQueryResponse,
  MediaQueryResponse,
  OnboardingQueryResponse,
  ProductIntroQueryResponse,
} from './types';

const NEXT_PUBLIC_RTE_URL = process.env.NEXT_PUBLIC_RTE_URL;
// PLATFORM is set to a static value; it could be dynamic based on configuration if needed
const PLATFORM = 'platform_html';

// Filter Error Warnings from the server
const filterWarningsLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.errors) {
      response.errors = response.errors.filter((err) => {
        const errorType: string = (err as any).errorType || '';
        return !['DEGRADATION', 'WARNING'].includes(errorType);
      });
    }
    return response;
  });
});

const httpLink = createHttpLink({
  uri: NEXT_PUBLIC_RTE_URL,
});

// Middleware for setting the Authorization header and refreshing tokens if needed
const authLink = setContext(async (_, { headers }) => {
  const storedProfile = getStoredProfile();
  let token = storedProfile?.accessToken;
  let expires = storedProfile?.expires;

  if (token && expires) {
    // Check if the token has expired
    if (nowInSeconds() >= expires) {
      try {
        log('GRAPHQL-AUTH-MIDDLEWARE: Refresh the tokens', 'info');
        const refreshedProfile = await refreshToken(); // Refresh the token
        if (refreshedProfile) {
          token = refreshedProfile.access_token;
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
        log('GRAPHQL-AUTH-MIDDLEWARE: Refresh Token Fetch failed', 'error');
        // A 429 is handled by refreshToken(); re-throw it instead of treating it as an expired session.
        if (error instanceof Error && error.message === '429') {
          throw error;
        }
        throw new Error('Session expired. Please log in again.');
      }
    }

    // Attach platform and accessToken in the header
    token = btoa(JSON.stringify({ platform: PLATFORM, accessToken: token }));
  } else {
    // Fallback to platform-only header if no token is available
    token = btoa(JSON.stringify({ platform: PLATFORM }));
  }

  return {
    headers: {
      ...headers,
      Authorization: token,
      'x-client-platform': PLATFORM,
    },
  };
});

const isProd = process.env.NEXT_PUBLIC_BUILD_ENV === 'prod';
const isPreStage = process?.env?.NEXT_PUBLIC_RTE_URL?.includes('stg-pre');

export const APP_CONTAINER_ID =
  isProd || isPreStage ? 'contentid/app-crave' : 'contentid/app-watch-experience';

// Initialize Apollo Client with authLink and httpLink
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, filterWarningsLink, httpLink]),
  cache: new InMemoryCache(APOLLO_CLIENT_CACHE_POLICY_OPTIONS),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  devtools: {
    enabled: !isProd,
  },
});

export const flushCache = async () => {
  flush();
  return await apolloClient?.clearStore();
};

export const useAppQuery = (
  sessionContext: SessionContext,
  options?: { skip?: boolean }
) => {
  const { loading, error, data, refetch } = useQuery<AppQueryResponse>(appQuery, {
    variables: { sessionContext, appId: APP_CONTAINER_ID },
    fetchPolicy: 'no-cache',
    skip: Boolean(options?.skip),
  });
  return {
    loading,
    error,
    refetch,
    app: data?.app,
  };
};

export const useProductIntro = (sessionContext: SessionContext) => {
  const { loading, error, data, responseHeaders } = useGraphQLQuery<
    ProductIntroQueryResponse,
    { sessionContext: SessionContext; appId: string }
  >({
    query: productIntroQuery,
    variables: { sessionContext, appId: APP_CONTAINER_ID },
    queryKey: queryKeys.productIntro(APP_CONTAINER_ID, sessionContext),
    withHeaders: true,
  });
  return {
    loading,
    error,
    productIntro: data?.productIntro,
    ...(responseHeaders && { responseHeaders }),
  };
};

export const useOnboarding = (sessionContext: SessionContext) => {
  const { loading, error, data, responseHeaders } = useGraphQLQuery<
    OnboardingQueryResponse,
    { sessionContext: SessionContext; appId: string }
  >({
    query: onboardingQuery,
    variables: { sessionContext, appId: APP_CONTAINER_ID },
    queryKey: queryKeys.onboarding(APP_CONTAINER_ID, sessionContext),
    withHeaders: true,
  });
  return {
    loading,
    error,
    onboarding: data?.onboarding,
    ...(responseHeaders && { responseHeaders }),
  };
};

export type UserInformationQueryResponse = {
  userAccountInfo: UserAccount;
};

export const useUserExperiences = () => {
  const [execute, { loading, error, data }] =
    useLazyGraphQLQuery<UserInformationQueryResponse>({
      query: userInformationQuery,
    });

  return {
    get: execute,
    loading,
    error,
    data,
  };
};

export const useMediaByID = (sessionContext: SessionContext, mediaId: number) => {
  const { loading, error, data, refetch } = useQuery<MediaQueryResponse>(
    getMediaByIDsQuery,
    {
      variables: { sessionContext, ids: [mediaId] },
      fetchPolicy: 'no-cache',
    }
  );

  if (error)
    throw getCraveError(
      CraveErrorCode.MEDIA_ID_NOT_FOUND_1002,
      sessionContext.userLanguage
    );

  return {
    refetch,
    loading,
    error,
    media: data?.medias[0],
  };
};

type DisplayName = { displayName: string };

/** Response shape of `getMoreInfoMediaByIDsQuery` — only what the More Info screen renders. */
export type MoreInfoMedia = {
  title?: string | null;
  description?: string | null;
  genres?: Array<DisplayName | null> | null;
  maturityRatings?: Array<DisplayName | null> | null;
  metadataImages?: { poster?: { url: string } | null } | null;
  details?: {
    cast?: Array<{ role: string; members: DisplayName[] } | null> | null;
  } | null;
};

export const useMoreInfoMediaByID = (sessionContext: SessionContext, mediaId: string) => {
  const { loading, error, data } = useQuery<{ medias: MoreInfoMedia[] | null }>(
    getMoreInfoMediaByIDsQuery,
    {
      variables: getMoreInfoQueryVariables(sessionContext, mediaId),
      // cache-first so a focus prefetch (or a previous visit) renders without a round-trip.
      fetchPolicy: 'cache-first',
      skip: !mediaId,
      errorPolicy: 'none',
    }
  );
  const media = data?.medias?.[0];

  // No id in the URL, a failed request, or an empty result all leave nothing to show.
  if (!mediaId || error || (data && !media))
    throw getCraveError(
      CraveErrorCode.MEDIA_ID_NOT_FOUND_1002,
      sessionContext.userLanguage
    );

  return {
    loading,
    media,
  };
};

export const useShowPageMediaByID = (sessionContext: SessionContext, mediaId: number) => {
  const { loading, error, data, refetch } = useQuery<MediaQueryResponse>(
    getShowPageMediaByIDsQuery,
    {
      variables: { sessionContext, ids: [mediaId] },
      fetchPolicy: 'no-cache',
    }
  );

  if (error)
    throw getCraveError(
      CraveErrorCode.MEDIA_ID_NOT_FOUND_1002,
      sessionContext.userLanguage
    );

  // a non-existent asset ID returns a different response. No retries for this or else we are stuck in retry loop.
  if (!loading && !data?.medias?.length)
    throw {
      ...getCraveError(
        CraveErrorCode.MEDIA_ID_NOT_FOUND_1002,
        sessionContext.userLanguage
      ),
      retry: false,
    };

  return {
    refetch,
    loading,
    error,
    media: data?.medias[0],
  };
};

export const useDeferredShowPageMediaByID = (
  sessionContext: SessionContext,
  mediaId: number,
  options?: { skip?: boolean }
) => {
  const { loading, error, data, refetch } = useQuery<MediaQueryResponse>(
    getDeferredMediaByIDsQuery,
    {
      variables: { sessionContext, ids: [mediaId] },
      fetchPolicy: 'no-cache',
      skip: Boolean(options?.skip),
    }
  );

  if (error)
    throw getCraveError(
      CraveErrorCode.MEDIA_ID_NOT_FOUND_1002,
      sessionContext.userLanguage
    );

  return {
    refetch,
    loading,
    error,
    media: data?.medias[0],
  };
};

/**
 * Minimal response type for the postersByIds query — only the fields needed to render
 * a poster card. Apollo auto-injects `__typename`, so items satisfy `MetadataItem`
 * and can be passed directly to `<Card item={item} />`.
 */
export type MediaPosterItem = {
  __typename: 'MediaMetadata';
  id: string;
  title: string;
  metadataImages?: {
    poster?: { url: string } | null;
  } | null;
};

type MediaPostersQueryResponse = {
  medias: MediaPosterItem[];
};

/**
 * Fetches only poster image URLs for a list of crave.ca content URLs.
 *
 * Parses the numeric media ID from each URL slug (e.g. `euphoria-45159` → `"45159"`)
 * and fires a single minimal GraphQL request. URLs that cannot be parsed are silently
 * skipped. Pass results directly to `<Card item={item} />`.
 *
 * @example
 * const { items } = useMediaPostersByContentUrls(
 *   { userLanguage, userMaturity },
 *   ['https://www.crave.ca/en/series/euphoria-45159']
 * );
 */
export const useMediaPostersByContentUrls = (
  sessionContext: SessionContext,
  contentUrls: string[]
) => {
  const ids = contentUrls
    .map(parseMediaIdFromContentUrl)
    .filter((id): id is string => id !== null);

  const { loading, error, data } = useQuery<MediaPostersQueryResponse>(
    getPostersByIdsQuery,
    {
      variables: { sessionContext, ids },
      skip: ids.length === 0,
    }
  );

  return {
    loading,
    error,
    items: data?.medias ?? [],
  };
};

export { useMyListQuery } from './hooks/useMyListQuery';
